import React, {
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Checkbox,
	Col,
	Flex,
	Row,
	Tabs,
	Typography,
} from 'antd';
import { useQuery } from 'react-query';
import { groupByProperty } from '@shared/utils/array';
import type { FixingCompanyWithLogo } from '@api/features/fixing-companies/getFixingCompanies';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import EditableTable, { EditableColumn } from '@client/components/EditableTable';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import AddButton from '@client/components/AddButton';
import {
	createFixingCompany,
	getFixingCompanies,
	getOrgBankAccounts,
	updateFixingCompany,
} from '@client/lib/api';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import { useAuth } from '@client/lib/auth';
import showErrorNotification from '@client/utils/showErrorNotification';
import FixingCompanyDetails from './FixingCompanyDetails';
import getFixingCompanyItems, { FixingCompanyDetailsValues } from './helpers/getFixingCompanyItems';

const NEW_FIXING_COMPANY_KEY = 'new';

const FixingCompanies = () => {
	const { data: fixingCompanies, refetch: refreshFixingCompanyList } = useQuery(
		['fixingCompanies'],
		getFixingCompanies,
	);
	const { refreshAuthInfo } = useAuth();
	const { data: bankAccounts, refetch: refreshBankAccounts } = useQuery(
		['orgBankAccounts', true],
		() => getOrgBankAccounts(true),
	);
	const [creating, setCreating] = useState(false);
	const [selectedKey, setSelectedKey] = useState<string | undefined>();
	const { makeBlockable } = useNavigationBlock();

	const bankAccountsByFixingCompany = useMemo(() => groupByProperty(
		[...(bankAccounts ?? [])].sort((a, b) => a.id - b.id),
		(a) => String(a.fixingCompanyId),
	), [bankAccounts]);

	const activeKey = (
		selectedKey === NEW_FIXING_COMPANY_KEY && !creating ? undefined : selectedKey
	) ?? fixingCompanies?.[0]?.id.toString();

	const refreshFixingCompanies = async () => {
		await refreshFixingCompanyList();
		refreshAuthInfo();
	};

	const selectTab = makeBlockable((key: string) => setSelectedKey(key));

	const startCreating = () => {
		setCreating(true);
		setSelectedKey(NEW_FIXING_COMPANY_KEY);
	};

	const onCreate = async (values: Partial<FixingCompanyDetailsValues>) => {
		const fixingCompany = await createFixingCompany({
			name: values.name ?? '',
			address: values.address ?? '',
			city: values.city ?? '',
			country: values.country ?? '',
			postalCode: values.postalCode,
			vatNumber: values.vatNumber,
		});

		await refreshFixingCompanies();
		setSelectedKey(fixingCompany.id.toString());
	};

	const onSaveRow = async (id: number, row: FixingCompanyProps) => {
		try {
			await updateFixingCompany(id, {
				active: row.active,
				isDefault: row.isDefault,
			});
		} catch (e) {
			showErrorNotification('Could not update Fixing Company', e as Error);
		}

		await refreshFixingCompanies();
	};

	const columns: Array<EditableColumn<FixingCompanyWithLogo, 'id'>> = [
		{
			dataIndex: 'name',
			title: 'Fixing Company',
		},
		{
			dataIndex: 'active',
			title: 'Active',
			align: 'center',
			editable: true,
			editingProps: {
				type: 'check',
				inputProps: {},
			},
			render: (v: boolean) => (<Checkbox checked={v} disabled />),
		},
		{
			dataIndex: 'isDefault',
			title: 'Default',
			align: 'center',
			editable: true,
			editingProps: {
				type: 'check',
				inputProps: {},
			},
			render: (v: boolean) => (<Checkbox checked={v} disabled />),
		},
	];

	return (
		<Row gutter={[24, 16]}>
			<Col xs={24} xl={8}>
				<Flex vertical gap={16}>
					<Alert message="Bank accounts created here will appear as options for all vessels" />
					<Flex align="center" gap={5}>
						<Typography.Title level={4} style={{ margin: 0 }}>
							Fixing Companies
						</Typography.Title>
						<AddButton
							data-testid="add-fixing-company"
							onClick={startCreating}
							disabled={creating}
							size="small"
						/>
					</Flex>
					<EditableTable<FixingCompanyWithLogo, 'id'>
						size="small"
						columns={columns}
						iconButtons
						keyDataIndex="id"
						onSave={onSaveRow}
						dataSource={fixingCompanies ?? []}
						pagination={false}
						enableDelete={false}
						addButtonInHeader
						onRowClick={(row) => selectTab(row.id.toString())}
					/>
				</Flex>
			</Col>
			<Col xs={24} xl={16}>
				<Tabs
					activeKey={activeKey}
					onChange={selectTab}
					items={[
						...(fixingCompanies ?? []).map((fixingCompany) => ({
							key: fixingCompany.id.toString(),
							label: fixingCompany.name,
							children: (
								<FixingCompanyDetails
									fixingCompany={fixingCompany}
									fixingCompanies={fixingCompanies ?? []}
									bankAccounts={bankAccountsByFixingCompany[fixingCompany.id] ?? []}
									refreshFixingCompanies={refreshFixingCompanies}
									refreshBankAccounts={refreshBankAccounts}
								/>
							),
						})),
						...(creating ? [{
							key: NEW_FIXING_COMPANY_KEY,
							label: 'New Fixing Company',
							children: (
								<EditableDetails<FixingCompanyDetailsValues>
									title="Company details"
									items={getFixingCompanyItems()}
									editing
									enableAutoSave={false}
									onSave={onCreate}
									onCancel={() => setCreating(false)}
								/>
							),
						}] : []),
					]}
				/>
			</Col>
		</Row>
	);
};

export default FixingCompanies;
